/**
 * NewPaywallPageDialog — 新建支付页面弹窗（ADM-PAY-S001 · new-paywall）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx（NewPaywallPageDialog）+ 接真 paymentPagesApi.create。
 * 原型按「预设模板」推导 scenario（pg_recharge_…），但 server 契约要求 scenario ∈ scenario 闭集
 *  （charge_center/limited_offer/new_user_offer/operational_popup，否则写出 C 端死配置）——
 * 故本接真版把「模板预设」收敛为「支付场景 scenario」卡片网格（= scenario），预填 name 文案。
 * 场景卡片展示 PAYMENT_SCENARIOS 全集（共享 ~/ui/ScenarioPicker、内建排前的展示排序副本）：
 *  内建场景（charge_center / new_user_offer / reading_pass / novel_purchase，由 App 创建时预置）+ 该 App 已存在 page 的 scenario 一律
 *  **禁用并标记徽章**（系统预置 / 已存在）防重复创建，仅未占用场景可选作运营新增页。
 * 「生效 App」：顶栏具体 App 作用域时只读展示（AppIcon + 名 + mono id，归属由顶部作用域决定，照原型）；
 *  仅聚合 'all' 作用域保留 Select 兜底（接真必要：创建必须落到一个具体 App）。
 * 接真：create(CreatePaymentPageRequest{app_id, scenario=scenario, name})；上架=create 后 update(id,{is_active:true})。
 * 成功 → toast + invalidate ['payment-pages', appId] + onClose；失败 → 错误 toast（不假成功）。
 * 原型 DialogShell → ~/ui/Dialog 的 Dialog 壳；表单行用共享 ~/ui/FormRow；window.toast → ~/ui/Toast。
 */

import type { CreatePaymentPageRequest } from '@novel-admin/shared';
import { BUILT_IN_PAYMENT_SCENARIOS, PAYMENT_SCENARIOS } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AppRow } from '~/data/queries';
import { paymentPagesApi } from '~/hooks/use-payments';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { ScenarioPicker } from '~/ui/ScenarioPicker';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

/** 当前 App 上下文（id/name 必备；color/icon 供 AppIcon 只读展示，聚合占位对象无 → AppIcon 落 accent 兜底）*/
interface DialogApp {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

/**
 * 支付场景（= payment_pages.scenario 闭集，与 server CreatePaymentPageRequest 一致）。
 * 卡片网格展示 PAYMENT_SCENARIOS 全集，不再过滤掉内建——
 * charge_center / new_user_offer / reading_pass / novel_purchase 为内建场景（App 创建时预置），
 * 连同该 App 已存在 page 的 scenario 一律禁用并标徽章防重复创建，仅未占用场景可选。
 */
// 必须与 server 侧 payment-pages-admin-service 的 BUILT_IN_SCENARIOS 一致：
// 少一个值这里就会把它当成「可自建可删」放出来，而 server 那边又按内建拒删。
const BUILT_IN_SCENARIOS = new Set<string>(BUILT_IN_PAYMENT_SCENARIOS);
const SCENARIO_META: Record<string, { name: string; desc: string }> = {
  charge_center: { name: '充值中心', desc: '标准 COIN 充值（内建）' },
  reading_pass: { name: '小时阅读卡', desc: '畅读卡管理页与阅读器内购买弹窗共用（内建）' },
  novel_purchase: { name: '书籍买断', desc: '书籍详情页买断弹窗（内建）' },
  limited_offer: { name: '限时特惠', desc: '营销节点高额 BONUS（Promo offer）' },
  new_user_offer: { name: '新人优惠', desc: '新人优惠页独立支付场景（内建）' },
  operational_popup: { name: '运营弹窗', desc: '运营弹窗统一支付场景' },
};

/**
 * 展示排序副本（照原型 PRESETS 顺序）：内建场景（App 预置）排前，其余保持 PAYMENT_SCENARIOS
 * 闭集相对顺序——只影响卡片渲染顺序，不动 shared 契约常量本身。
 */
const DISPLAY_SCENARIOS = [
  ...PAYMENT_SCENARIOS.filter((s) => BUILT_IN_SCENARIOS.has(s)),
  ...PAYMENT_SCENARIOS.filter((s) => !BUILT_IN_SCENARIOS.has(s)),
];

/** 第一个未被占用（既非内建、也未在该 App 已存在）的 scenario；全占用时返回 ''（不预选被禁用卡片，由 canSubmit 拦截）。 */
function firstSelectableScenario(existingScenarios: Set<string>): string {
  return (
    PAYMENT_SCENARIOS.find((s) => !BUILT_IN_SCENARIOS.has(s) && !existingScenarios.has(s)) ?? ''
  );
}

/**
 * 本对话框的 FormRow 别名 — 逐字对齐原型 dialogs.jsx 顶部
 * `const FormRow = (p) => <UI.FormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p}/>`，
 * 收口共享 ~/ui/FormRow（左标签列布局），删除本文件早先的 label-on-top 本地副本。
 */
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

export function NewPaywallPageDialog({
  app,
  apps,
  onClose,
  existingScenarios = new Set<string>(),
}: {
  app?: DialogApp;
  apps: AppRow[];
  onClose: () => void;
  /** 该 App 已存在 page 的 scenario 集合（由调用方 PaywallPage 传入）；命中者禁用并标「已存在」防重复创建。 */
  existingScenarios?: Set<string>;
}) {
  const queryClient = useQueryClient();
  // 可选 App 全集排除聚合视图（原型按 a.id !== "all" 过滤；此处 apps 已是真实列表，保留同语义）
  const APPS = apps.filter((a) => a.id !== 'all');
  // scenario = scenario 闭集（server 契约）。初始选中第一个未占用场景（既非内建、也未在该 App 已存在）；
  // existingScenarios 初次渲染即可用，惰性初始化 useState 一次定调，name 预填该场景默认名（非空可直接提交）。
  const [scenario, setScenario] = useState<string>(() =>
    firstSelectableScenario(existingScenarios),
  );
  const [name, setName] = useState(
    () => SCENARIO_META[firstSelectableScenario(existingScenarios)]?.name ?? '',
  );
  // 顶栏具体 App 作用域（app.id !== 'all'）→「生效 App」只读展示（归属由顶部作用域决定，照原型）；
  // 聚合 'all' / 无 app 时才走 Select 兜底（接真必要：创建必须落到一个具体 App）
  const scopedApp = app && app.id !== 'all' ? app : undefined;
  const [appId, setAppId] = useState(scopedApp?.id || APPS[0]?.id || '');
  // scenario 全占用时初始为 ''：必须选中有效场景才可提交
  const canSubmit = !!name.trim() && !!appId && !!scenario;

  const createMutation = useMutation({
    mutationFn: async ({ publish }: { publish: boolean }) => {
      const body: CreatePaymentPageRequest = {
        app_id: appId,
        scenario: scenario as CreatePaymentPageRequest['scenario'],
        name: name.trim(),
      };
      const created = await paymentPagesApi.create(body);
      // 「创建并上架」：create 默认下架（is_active=false）→ 再 update 翻 is_active=true
      if (publish) await paymentPagesApi.update(created.id, { is_active: true });
      return { created, publish };
    },
    onSuccess: async ({ created, publish }) => {
      toast(`${publish ? '已创建并上架' : '已创建草稿'} · ${created.name}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await queryClient.invalidateQueries({ queryKey: ['payment-pages', appId] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '创建失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const submit = (publish: boolean) => {
    if (!canSubmit || createMutation.isPending) return;
    createMutation.mutate({ publish });
  };

  // 预填名称：切场景时若 name 仍空/等于上个场景默认名，跟随场景默认名
  const onScenarioChange = (next: string) => {
    // 双保险：目标场景 disabled（内建 / 该 App 已存在）则忽略，不允许选中
    if (BUILT_IN_SCENARIOS.has(next) || existingScenarios.has(next)) return;
    setScenario(next);
    const prevDefault = SCENARIO_META[scenario]?.name ?? '';
    if (!name.trim() || name === prevDefault) setName(SCENARIO_META[next]?.name ?? '');
  };

  return (
    <Dialog
      width={640}
      title="新建支付页面"
      subtitle="付费页面是客户端展示给用户的购买入口，创建后可在详情按渠道配置 placement"
      icon="IconWallet"
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            草稿默认下架 · 完善 placement 后再上架；创建并上架则立即对客户端可见
          </span>
          <button className="btn" onClick={onClose} disabled={createMutation.isPending}>
            取消
          </button>
          <button
            className="btn"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => submit(false)}
          >
            保存草稿
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => submit(true)}
          >
            <Icons.IconCheck size={11} />
            {createMutation.isPending ? '创建中…' : '创建并上架'}
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="页面名称" required hint="运营内部识别 · 客户端文案在档位中分别维护">
          <input
            className="input"
            placeholder="如：会员中心"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ maxWidth: 380 }}
          />
        </FormRow>

        {scopedApp ? (
          <FormRow label="生效 App" hint="支付页面归属当前管理的 App，由顶部作用域决定">
            <div className="hstack" style={{ gap: 8 }}>
              <AppIcon app={scopedApp} size="sm" />
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{scopedApp.name}</span>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                {appId}
              </span>
            </div>
          </FormRow>
        ) : (
          <FormRow label="生效 App" required hint="一个付费页面归属一个 App">
            <Select
              className="input"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              style={{ maxWidth: 280 }}
            >
              {APPS.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </FormRow>
        )}

        <FormRow
          label="支付场景 (scenario)"
          required
          hint="scenario = 客户端拉取 paywall 的 scenario · 内建场景由 App 预置"
        >
          {/* 共享 ScenarioPicker（原型 UI.ScenarioPicker）：内建场景标「系统预置」、
              该 App 已存在 page 的 scenario 标「已存在」，均禁用防重复创建 */}
          <ScenarioPicker
            items={DISPLAY_SCENARIOS.map((s) => {
              const isBuiltIn = BUILT_IN_SCENARIOS.has(s);
              const existing = existingScenarios.has(s);
              return {
                value: s,
                label: SCENARIO_META[s]?.name ?? s,
                desc: SCENARIO_META[s]?.desc,
                builtin: isBuiltIn,
                disabled: isBuiltIn || existing,
                chipText: !isBuiltIn && existing ? '已存在' : undefined,
              };
            })}
            value={scenario}
            onSelect={onScenarioChange}
          />
        </FormRow>

        <div
          style={{
            marginTop: 14,
            padding: 10,
            background: 'var(--info-soft)',
            borderRadius: 5,
            fontSize: 11.5,
            color: 'var(--info)',
          }}
        >
          <Icons.IconInfo size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          创建后在页面详情按各支付渠道分别配置 Adapty placement_id，配好再保存并上架。
        </div>
      </div>
    </Dialog>
  );
}
